import React, { useContext, useEffect, useState } from 'react';
import { TranslationKey } from '../../../types/translation/TranslationKey';
import { t } from '../../../types/translation/Translator';
import DropdownSelect from '../../pages/Common/DropdownSelect';
import { CsvUploadConfiguration, CsvUploadType, ventoryFieldToText } from '../../../types/csvUploadConfiguration';
import { CompanyContext } from '../../../context/CompanyContext';
import { useMutation } from '@apollo/client';
import {
  CreateCsvUploadConfigurationResponse,
  CreateCsvUploadConfigurationVariables,
  CsvUploadConfigurationMutations,
  CsvUploadResponse,
  CsvUploadVariables,
} from '../../../graphql/csvUploadConfiguration.graphql';
import { CustomFieldContext } from '../../../context/CustomFieldContext';
import { CsvUploadConfigurationContext } from '../../../context/CsvUploadConfigurationContext';
import { useToast } from '../../../providers/ToastProvider';
import { ToastType } from '../Toast';
import { ModalPane } from '../../../VentoryUI/components/common/Modal';
import { TestIdIdentifier, testIds } from '../../../util/identifiers/identifiers.util';
import { Grid } from '@mui/material';
import { TextInput } from '../TextInput';
import { Feedback } from '../Feedback/feedback';
import { cloneDeep } from '@apollo/client/utilities';
import { NextButton, BackButton } from '../Button';

interface CreateCsvUploadConfigurationMappingInputProps {
  required: string[];
  optional: string[];
  headers: string[];
  type: CsvUploadType;
  onClose: () => void;
  onBack: () => void;
  stockLocationId?: string;
  csvFileAsBase64Data: string;
  setError: (err: string) => void;
  handleUploadOverride?: (csvFileAsBase64Data: string, configurationId: string) => Promise<void>;
  handleUploadError: (newConfiguration: CsvUploadConfiguration | undefined) => void;
  handleSelectedHeadersChanged?: (selectedHeaders: Set<string>) => void;
}

export default function CreateCsvUploadConfigurationMapping({
  required,
  optional,
  headers,
  type,
  onClose,
  onBack,
  stockLocationId,
  csvFileAsBase64Data,
  setError,
  handleUploadOverride,
  handleUploadError,
  handleSelectedHeadersChanged,
}: CreateCsvUploadConfigurationMappingInputProps) {
  const { showToast } = useToast();
  const { currentCompany } = useContext(CompanyContext);
  const { customFields } = useContext(CustomFieldContext);
  const { csvUploadConfigurations, setCsvUploadConfigurations } = useContext(CsvUploadConfigurationContext);

  const [upload, { loading: uploadLoading }] = useMutation<CsvUploadResponse, CsvUploadVariables>(
    CsvUploadConfigurationMutations.upload,
  );

  const handleUpload = async (configuration: CsvUploadConfiguration) => {
    await upload({
      variables: {
        configurationId: configuration.id,
        input: {
          companyId: currentCompany.id,
          stockLocationId: stockLocationId || '',
          type,
          csvFileAsBase64Data,
        },
      },
      onCompleted: res => (res.csvUpload.upload === true ? onClose() : null),
      onError: error => {
        showToast({ type: ToastType.error, title: error.message });
        handleUploadError(configuration);
      },
    });
  };

  const [create, { loading: createLoading }] = useMutation<
    CreateCsvUploadConfigurationResponse,
    CreateCsvUploadConfigurationVariables
  >(CsvUploadConfigurationMutations.create, {
    onCompleted: async res => {
      const createdConfig = res.createCsvUploadConfiguration[0];
      if (!createdConfig) {
        return setError(t(TranslationKey.somethingUnexpectedHappened).singular.label);
      }
      csvUploadConfigurations.set(createdConfig.id, createdConfig);
      setCsvUploadConfigurations(new Map(csvUploadConfigurations));

      if (handleUploadOverride) handleUploadOverride(csvFileAsBase64Data, createdConfig.id);
      else handleUpload(createdConfig);
    },
    onError: err => setError(err.message),
  });

  const [csvUploadConfigurationInput, setCsvUploadConfigurationInput] = useState<CsvUploadConfiguration>(
    new CsvUploadConfiguration({ companyId: currentCompany.id, type: type, headerNames: headers }),
  );

  const [alreadySelectedHeaders, setAlreadySelectedHeaders] = useState<Set<string>>(new Set());

  const handleCreate = async () => {
    await create({
      variables: {
        csvUploadConfigurations: [csvUploadConfigurationInput.forCreate()],
      },
    });
  };

  const handleMappingChange = (value: string | null, item: string) => {
    if (value) {
      csvUploadConfigurationInput.withMapping(item, value);
      setAlreadySelectedHeaders(new Set(alreadySelectedHeaders.add(value)));
    } else {
      const oldValue = csvUploadConfigurationInput.mapping.get(item);
      if (!oldValue) return;
      csvUploadConfigurationInput.removeMapping(item);
      alreadySelectedHeaders.delete(oldValue);
      setAlreadySelectedHeaders(new Set(alreadySelectedHeaders));
    }

    setCsvUploadConfigurationInput(cloneDeep(csvUploadConfigurationInput));
  };

  useEffect(() => {
    if (handleSelectedHeadersChanged) {
      handleSelectedHeadersChanged(new Set([...csvUploadConfigurationInput.mapping.keys()]));
    }
  }, [csvUploadConfigurationInput]);

  const configurationWithNameExists = (name: string) => {
    return [...csvUploadConfigurations.values()].some(c => c.type === type && c.name === name);
  };

  return (
    <ModalPane
      testId={testIds.createCsvUploadConfigurationMapping}
      overflow='auto'
      footer={
        <Grid container columnSpacing={1} height={'100%'} justifyContent={'flex-end'}>
          <Grid item marginTop={'auto'}>
            <BackButton onClick={onBack} disabled={createLoading || uploadLoading} />
          </Grid>
          <Grid item marginTop={'auto'}>
            <NextButton
              disabled={
                !csvUploadConfigurationInput.name ||
                !required.every(r => csvUploadConfigurationInput.mapping.has(r)) ||
                configurationWithNameExists(csvUploadConfigurationInput.name)
              }
              onClick={handleCreate}
              loading={createLoading || uploadLoading}
            />
          </Grid>
        </Grid>
      }
    >
      <Grid
        container
        height={'100%'}
        justifyItems={'space-between'}
        data-testid={testIds.createCsvUploadConfigurationMapping}
      >
        <Grid item xs={12}>
          <Grid container rowSpacing={2}>
            <Grid item xs={12}>
              <p className='rounded-lg text-sm font-semibold'>
                {'No existing CSV configurations were found for this file, please create one instead.'}
              </p>
            </Grid>
            <Grid item xs={12}>
              <TextInput
                mandatory
                label={t(TranslationKey.name).singular.label}
                placeholder={t(TranslationKey.csvUploadConfigurationNamePlaceholder).singular.label}
                onChange={v => setCsvUploadConfigurationInput(csvUploadConfigurationInput.withName(v))}
                value={csvUploadConfigurationInput.name}
                feedback={value => {
                  if (configurationWithNameExists(value)) {
                    return {
                      text: t(TranslationKey.csvUploadConfigurationNameAlreadyExists).singular.label,
                      type: Feedback.Type.error,
                    };
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <p className='text-sm font-semibold'>{t(TranslationKey.mapTheFollowingFields).singular.label}</p>
            </Grid>
            <Grid item xs={12} ml={1} height={'395px'} overflow={'auto'}>
              <Grid container>
                <Grid item xs={10}>
                  {required.map(item => (
                    <Grid key={item} container>
                      <Grid item width={'250px'} marginY={'auto'}>
                        <Grid container>
                          <Grid item>
                            <p className='text-sm'>{ventoryFieldToText(item, customFields)}</p>
                          </Grid>
                          <Grid item>
                            <p className='ml-1 text-sm font-medium text-red-500'>*</p>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item width={'300px'} marginY={'auto'}>
                        <DropdownSelect
                          testId={new TestIdIdentifier(item)}
                          maxHeight='150px'
                          values={headers.filter(h => !alreadySelectedHeaders.has(h))}
                          selectedValue={csvUploadConfigurationInput.mapping.get(item) || null}
                          toText={v => v}
                          onChange={v => handleMappingChange(v, item)}
                        />
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
                <Grid item xs={10}>
                  {optional.sort().map(item => (
                    <Grid key={item} container>
                      <Grid item width={'250px'} marginY={'auto'}>
                        <Grid container>
                          <Grid item>
                            <p className='text-sm'>{ventoryFieldToText(item, customFields)}</p>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item width={'300px'} marginY={'auto'}>
                        <DropdownSelect
                          maxHeight='150px'
                          testId={new TestIdIdentifier(item)}
                          values={headers.filter(h => !alreadySelectedHeaders.has(h))}
                          selectedValue={csvUploadConfigurationInput.mapping.get(item) || null}
                          toText={v => v}
                          onChange={v => handleMappingChange(v, item)}
                        />
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </ModalPane>
  );
}
